import initialGenerator from '@/scripts/helpers/initial-generator';
import { Helper, Model } from '@viddsee/server-api-client';
import { UserGender } from '@/scripts/models/UserResponse';
import { UserUnlockable, ProgressLevel } from '@viddsee/server-api-client/src/models/me/profile';

const facebookImageRegex = /graph\.facebook\.com\/([0-9]*)\/picture/;

/**
 * Resolve the image dynamically based on the user profile url
 * @param {Number} [size] size of image to obtain
 * @param {String} [url] that will be used with highest priority
 * @param {String} [name] that will be used to create an initial if no image is available
 * @return {String} url string that can be used in a img src
 */
export const normalizeImage = function normalizeImage(size: number, url: string, name: string): string {
  switch (true) {
    case !url:
      return initialGenerator.make({
        name,
      });
    case facebookImageRegex.test(url):
      const [, fbUserId] = url.match(facebookImageRegex) || [];
      const fbUrl = `https://graph.facebook.com/${fbUserId}/picture`;
      return size ? `${fbUrl}?height=${size}&width=${size}` : fbUrl;
    default:
      return url;
  }
};

/**
 * @param {Object} response object obtained remotely that represent a user
 * @return a function
 */
const resolveImage = function resolveImage(response: Model.Me.Profile.Info): (size: number) => string {
  const url = response.profileImageUrl;
  return (size: number): string => normalizeImage(size, url, `${response.firstName} ${response.lastName}`);
};

// TODO: Due to server side response inconsistency between unverification behaviour.
//       From the client side, I will need to clean up response first to keep the front end logic less complicated
//       See: https://github.com/viddsee/viddsee_api/issues/112#issuecomment-318987195
const getProcessedEmail = (res: any): UserInfoCompat => {
  const result: any = Helper.convertToCamelCase({ ...res });

  if (result.isEmailVerified === false) {
    // Case: User has both primary_email and unverified email.
    //       In this case, unverified_email take precedence,
    //       the existing primary email should be ignored and removed.
    if (result.unverifiedEmail && result.primaryEmail) {
      result.primaryEmail = '';

    // Case: User has an unverified primary email.
    //       To keep the behaviour consistent, we will move primary_email to unverified_email
    //       The primary_email field should be ignored and removed.
    } else if (result.primaryEmail) {
      result.unverifiedEmail = result.primaryEmail;
      result.primaryEmail = '';

    // Case: User has an unverified primary email.
    //       But primary_email is actually empty
    //       Keep it that way
    } else {
      result.primaryEmail = '';
    }
  } else {
    // Case: At this moment, unverified_email field is not cleared after verification.
    //       See: https://github.com/viddsee/viddsee_api/issues/115
    if (result.unverifiedEmail === result.primaryEmail) {
      delete result.unverifiedEmail;

    // Case: A unverified_email exist and it is not the same with primary_email.
    //       This imply that user email is not verified
    } else if (result.unverifiedEmail) {
      // Do nothing
    }
  }
  // At the end, remove this is_email_verified
  delete result.isEmailVerified;

  return result;
};

export interface UserInfoCompat {
  id: number;
  pid: string;
  adminAccess: boolean;
  profile: {
    vanity: string;
    firstName: string;
    lastName: string;
    bio: string;
    hasFilms: boolean;
    birthday: string;
    labels: Array<string>;
    gender: UserGender | null;
    profileImageUrl: string;
    profileImage: (size: number) => string;
    hasPinSet: boolean;
    hasDoneDailyLogin: boolean;
    email: {
      primaryEmail: string | null;
      unverifiedEmail?: string;
    };
    socialMedia: {
      facebook: {
        fbId: string;
      };
    };
    settings: {
      email: {
        community: boolean;
        newsletter: boolean;
      };
      notification: {
        comment: boolean;
      };
      privacy: {
        showLikes: boolean;
      };
    };
  };
  points: {
    badges: Array<UserUnlockable>;
    level: ProgressLevel;
    experiencePoints: {
      current: number;
      requiredForNextLevel: number;
    };
  };
  stats: {
    hasFilms: boolean;
    hasStartedFollowing: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

/**
 * Remap the /me response object into hierarchy
 * This is an in-progress change
 * See: https://github.com/viddsee/viddsee_api/issues/347
 * @param {Object} res - the original flat response object from the server
 */
const getRemappedResponse = (rawResponse: any): UserInfoCompat => {
  // NOTE: Revamp
  // For legacy support, we will always camelize all properties.
  const res = Helper.convertToCamelCase(rawResponse) as Model.Me.Profile.Info & { unverifiedEmail?: string };
  const result: UserInfoCompat = {
    id: res.id,
    pid: res.profileId,
    adminAccess: false,
    profile: {
      vanity: res.vanity,
      firstName: (res.firstName || '').trim(),
      lastName: (res.lastName || '').trim(),
      bio: res.bio || '',
      hasFilms: res.hasFilms,
      birthday: res.birthday,
      gender: res.gender,
      labels: res.labels,
      profileImageUrl: res.profileImageUrl,
      hasPinSet: res.hasPinSet,
      hasDoneDailyLogin: res.hasDoneDailyLogin,
      profileImage: resolveImage(res),
      email: {
        ...(res.unverifiedEmail ? { unverifiedEmail: res.unverifiedEmail } : {}),
        primaryEmail: res.primaryEmail,
      },
      socialMedia: {
        facebook: {
          fbId: res.fbId,
        },
      },
      settings: {
        email: {
          community: res.isSubscribedToCommunityUpdateEmails,
          newsletter: res.isSubscribedToWeeklyNewsletter,
        },
        notification: {
          comment: res.isSubscribedToCommentNotificationEmails,
        },
        privacy: {
          showLikes: res.privacy && res.privacy.shouldShowLikes,
        },
      },
    },
    stats: {
      hasFilms: res.hasFilms,
      hasStartedFollowing: res.hasStartedFollowing,

      createdAt: res.createdAt,
      updatedAt: res.updatedAt,
    },
    points: {
      badges: res.badges,
      level: res.level,
      experiencePoints: res.experiencePoints,
    },
  };

  return result;
};

export default {
  getProcessedEmail,
  getRemappedResponse,
  normalizeImage,
};
